import { Box, Button, Fab, Paper, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { AutoAwesome } from '@mui/icons-material';
import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { useParams } from 'react-router-dom';
import { BlogModel } from '../model/Blog.model';
import { compiledBlogUrl, legacyBlogUrl } from '../blog/cdn';
import MdxRenderer from '../blog/MdxRenderer';
import BlogAvatar from '../components/BlogAvatar';
import TopicChips from '../components/TopicChips';
import TldrDialog from '../components/TldrDialog';
import Comments from '../components/Comments';
import { BLOG_API_BASE } from '../config/site';

// New posts are compiled MDX (<slug>/index.js); pre-MDX posts are still raw
// markdown (<slug>.md) at the bucket root and keep rendering via react-markdown.
type Loaded =
  | { kind: 'mdx'; code: string; slug: string }
  | { kind: 'legacy'; markdown: string };

const BlogDetail: React.FC = () => {
  const { id } = useParams();

  const [blog, setBlog] = useState<BlogModel>();
  const [loaded, setLoaded] = useState<Loaded>();
  const [tldrOpen, setTldrOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setBlog(undefined);
    setLoaded(undefined);
    setTldrOpen(false);

    fetch(`${BLOG_API_BASE}/getBlogs/${id}`)
      .then((res) => res.json())
      .then((json) => json as BlogModel)
      .then(async (b) => {
        if (cancelled) return;
        setBlog(b);
        const slug = b.location;

        // Prefer compiled MDX; fall back to legacy markdown if it isn't there.
        const mdx = await fetch(compiledBlogUrl(slug));
        if (mdx.ok) {
          const code = await mdx.text();
          if (!cancelled) setLoaded({ kind: 'mdx', code, slug });
          return;
        }
        const legacy = await fetch(legacyBlogUrl(slug));
        const markdown = await legacy.text();
        if (!cancelled) setLoaded({ kind: 'legacy', markdown });
      })
      .catch(() => {
        if (!cancelled) setLoaded({ kind: 'legacy', markdown: '_Could not load this post._' });
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  const blogHeader = blog ? (
    <Stack direction="row" spacing={{ xs: 2, sm: 3 }} alignItems="flex-start">
      <BlogAvatar
        name={blog.name}
        slug={blog.location}
        image={blog.image}
        size={{ xs: 56, sm: 88 }}
      />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* h3's default 3rem is wider than a phone can hold once the avatar
            takes its share: a single long word ("Honeymoon") has nowhere to
            wrap and spills out of the card. Scale the size down by breakpoint,
            and keep overflowWrap as a backstop for any word that still can't
            fit on one line. */}
        <Typography
          variant="h3"
          sx={{
            lineHeight: 1.15,
            fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' },
            overflowWrap: 'break-word',
          }}
        >
          {blog.name}
        </Typography>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 1 }}
        >
          <TopicChips topics={blog.topics} />
          <Typography variant="body2" color="text.secondary">
            {new Date(blog.created).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </Typography>
        </Stack>
        <Typography variant="body1" sx={{ mt: 1.5 }}>
          {blog.headline}
        </Typography>
      </Box>

      {/* Primary entry point, sitting with the title where it reads as part of
          the post's furniture rather than as a floating widget. */}
      <Button
        variant="contained"
        color="primary"
        startIcon={<AutoAwesome />}
        onClick={() => setTldrOpen(true)}
        sx={{ flexShrink: 0, display: { xs: 'none', sm: 'inline-flex' } }}
      >
        TLDR
      </Button>
    </Stack>
  ) : (
    <Stack direction="row" spacing={{ xs: 2, sm: 3 }} alignItems="flex-start">
      <Skeleton
        variant="circular"
        sx={{ flexShrink: 0, width: { xs: 56, sm: 88 }, height: { xs: 56, sm: 88 } }}
      />
      <Box sx={{ flex: 1 }}>
        <Skeleton variant="text" sx={{ width: '55%', fontSize: { xs: '1.75rem', sm: '3rem' } }} />
        <Skeleton variant="text" sx={{ width: '30%' }} />
        <Skeleton variant="text" sx={{ width: '80%' }} />
      </Box>
    </Stack>
  );

  let blogContent: React.ReactNode;
  if (!loaded) {
    blogContent = <Skeleton variant="rectangular" sx={{ width: '100%', height: '20em' }} />;
  } else if (loaded.kind === 'mdx') {
    blogContent = <MdxRenderer code={loaded.code} slug={loaded.slug} />;
  } else {
    blogContent = <ReactMarkdown>{loaded.markdown}</ReactMarkdown>;
  }

  return (
    <Box>
      <Paper elevation={8} sx={{ p: { xs: 2, sm: 4 }, mb: 4 }}>{blogHeader}</Paper>

      <Paper elevation={6} sx={{ p: { xs: 2, sm: 4 } }}>{blogContent}</Paper>

      {blog && <Comments blogId={blog.id} />}

      {/* Mobile-only counterpart to the header button, which is hidden below
          `sm` because there's no room for it beside the title. Exactly one of
          the two is visible at any width. */}
      {blog && (
        <Tooltip title="TLDR mode: summary and Q&A" placement="right">
          <Fab
            color="primary"
            onClick={() => setTldrOpen(true)}
            aria-label="Open TLDR mode"
            sx={{
              display: { xs: 'inline-flex', sm: 'none' },
              position: 'fixed',
              bottom: 24,
              left: 24,
              zIndex: (theme) => theme.zIndex.fab,
            }}
          >
            <AutoAwesome />
          </Fab>
        </Tooltip>
      )}

      {blog && (
        <TldrDialog
          open={tldrOpen}
          onClose={() => setTldrOpen(false)}
          blogId={blog.id}
          blogName={blog.name}
          summary={blog.summary}
        />
      )}
    </Box>
  );
};

export default BlogDetail;
